import { useEffect } from "react";

/**
 * GMA-1478: small reusable confirm dialog (SweetAlert-style), modeled on the
 * in-house DeleteConfirmationModal so the app gains no new dependency. Used to
 * gate destructive/bulk actions (e.g. the bell "Clear all") behind an explicit
 * confirmation so an accidental click can't fire them.
 */
const ConfirmModal = ({
  isOpen,
  isLoading = false,
  title = "Are you sure?",
  message,
  confirmLabel = "Confirm",
  cancelLabel = "Cancel",
  onConfirm,
  onCancel,
}) => {
  // Escape-to-close for keyboard/accessibility (ignored while loading), matching
  // other dialogs in the app. Declared before the early return to satisfy the
  // rules of hooks.
  useEffect(() => {
    if (!isOpen || isLoading) return undefined;
    const onKeyDown = (e) => {
      if (e.key === "Escape") onCancel();
    };
    document.addEventListener("keydown", onKeyDown);
    return () => document.removeEventListener("keydown", onKeyDown);
  }, [isOpen, isLoading, onCancel]);

  if (!isOpen) return null;

  return (
    <div
      data-testid="confirm-modal"
      role="dialog"
      aria-modal="true"
      aria-label={title}
      style={{
        position: "fixed",
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: "rgba(0, 0, 0, 0.6)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        zIndex: 1000,
      }}
      onClick={() => !isLoading && onCancel()}
    >
      <div
        style={{
          backgroundColor: "#fff",
          borderRadius: "12px",
          padding: "24px",
          maxWidth: "440px",
          width: "90%",
          boxShadow: "0 4px 20px rgba(0, 0, 0, 0.3)",
        }}
        onClick={(e) => e.stopPropagation()}
      >
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: "12px",
            marginBottom: "12px",
          }}
        >
          <span style={{ fontSize: "1.8rem" }} aria-hidden="true">
            🔔
          </span>
          <h2 style={{ margin: 0, color: "#333", fontSize: "1.3rem" }}>
            {title}
          </h2>
        </div>

        {message && (
          <p style={{ color: "#555", lineHeight: "1.6", marginBottom: "20px" }}>
            {message}
          </p>
        )}

        <div
          style={{
            display: "flex",
            gap: "12px",
            justifyContent: "flex-end",
          }}
        >
          <button
            type="button"
            data-testid="confirm-modal-cancel"
            onClick={onCancel}
            disabled={isLoading}
            style={{
              padding: "10px 20px",
              backgroundColor: "#e9ecef",
              border: "1px solid #dee2e6",
              borderRadius: "6px",
              cursor: isLoading ? "not-allowed" : "pointer",
              fontSize: "1rem",
              opacity: isLoading ? 0.6 : 1,
            }}
          >
            {cancelLabel}
          </button>
          <button
            type="button"
            data-testid="confirm-modal-confirm"
            onClick={onConfirm}
            disabled={isLoading}
            style={{
              padding: "10px 20px",
              backgroundColor: "#1976d2",
              color: "#fff",
              border: "1px solid #1976d2",
              borderRadius: "6px",
              cursor: isLoading ? "not-allowed" : "pointer",
              fontSize: "1rem",
              opacity: isLoading ? 0.6 : 1,
            }}
          >
            {isLoading ? "Working..." : confirmLabel}
          </button>
        </div>
      </div>
    </div>
  );
};

export default ConfirmModal;
